import { Sim, CROP_MAX_STAGE, PlotSystem } from '../../game/simulation';
import { ICON } from '../icons';
import { t } from '../strings';

/**
 * "Next step" pill at the top-center — reads sim state every tick and tells
 * the player what to do next.
 */
export class Objective {
  private root: HTMLDivElement;

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-objective';
    this.root.innerHTML = `
      <span class="ui-objective__star glyph">${ICON.goal}</span>
      <span class="ui-objective__label">Goal</span>
      <span data-field="text">—</span>
    `;
    parent.appendChild(this.root);
  }

  update(_dt: number) {
    const { text, warn } = this.computeGoal();
    const target = this.root.querySelector('[data-field="text"]');
    if (target) target.textContent = text;
    this.root.classList.toggle('warn', !!warn);
  }

  private computeGoal(): { text: string; warn?: boolean } {
    const s = this.sim.state;
    const rep = this.sim.storage();

    if (rep.totalUsed >= rep.totalCap && rep.totalUsed > 0)
      return { text: t('goal.storageFull'), warn: true };
    if (s.res.energy < 2) return { text: t('goal.energyLow'), warn: true };

    const cargo = s.truck.cargo;
    const cargoTotal = cargo.wheat + cargo.corn + cargo.carrot + cargo.flour + cargo.wood;
    if (cargoTotal > 0 && (s.truck.status === 'idle' || s.truck.status === 'loading'))
      return { text: t('goal.truckLoaded') };

    let hasMature = false;
    PlotSystem.forEachTile(s, (tile) => { if (tile.crop && tile.crop.stage >= CROP_MAX_STAGE) hasMature = true; });
    if (hasMature) return { text: t('goal.matureCrops') };

    let hasEmptyField = false;
    PlotSystem.forEachTile(s, (tile) => { if (tile.surf === 'field' && !tile.crop && !tile.bld) hasEmptyField = true; });
    if (hasEmptyField && (s.res.seeds.wheat | 0) > 0)
      return { text: t('goal.emptyField') };
    if (hasEmptyField && (s.res.seeds.wheat | 0) === 0)
      return { text: t('goal.noSeeds') };

    let hasForest = false;
    let hasGrass = false;
    PlotSystem.forEachTile(s, (tile) => {
      if (tile.thing) hasForest = true;
      if (tile.surf === 'grass' && !tile.thing && !tile.bld) hasGrass = true;
    });
    if (!hasEmptyField && hasGrass) return { text: t('goal.grass') };
    if (!hasEmptyField && hasForest) return { text: t('goal.forest') };

    return { text: t('goal.idle') };
  }
}
